import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { Check, X, RotateCcw, Shuffle, RefreshCw } from 'lucide-react';
import {
  msgErrore, fmtEur, fmtN, fmtData, fmtDataOra, Sezione, card, th, thSx, td, tdSx, pulsante, errore,
  SelectTipo, valoreTipo, corpoTipo, vociCanale, voceCanale,
} from './campagneComuni';

// Sezioni admin della pagina Campagne (campagne.gestisci): moduli e campagne da mappare, con la scelta
// "senza tipo, deciso", canale dei valori di utm_source e dei domini di provenienza (referrer) e collegamenti
// lead -> adesione da verificare.
// Ogni modifica va in audit_log (backend) e fa ricalcolare il report. Il canale delle campagne non si
// sceglie qui: lo assegna la sync dalla regola (Meta, Google Search, Demand Gen).

const COLORE_ADMIN = '#C53030';
const LIVELLI = { certo: 'Certo', probabile: 'Probabile', da_verificare: 'Da verificare', confermato: 'Confermato dal cliente' };
const COLORI_LIVELLO = { certo: '#276749', probabile: '#2B6CB0', da_verificare: '#B7791F', confermato: '#6B46C1' };
const STATI = { automatico: 'Automatico', confermato: 'Confermato', rifiutato: 'Rifiutato' };
const PIATTAFORME = { fb: 'Facebook', ig: 'Instagram', msg: 'Messenger', an: 'Audience Network' };

function Interruttore({ etichetta, valore, onChange }) {
  return (
    <label style={{ fontSize: 12, color: '#4A5568', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
      <input type="checkbox" checked={valore} onChange={e => onChange(e.target.checked)} /> {etichetta}
    </label>
  );
}

function ModuliDaMappare({ filtri, onCambio }) {
  const [moduli, setModuli] = useState([]);
  const [decisi, setDecisi] = useState(false);
  const [err, setErr] = useState(null);
  const carica = useCallback(async () => {
    try {
      const r = await API.get('/campagne/moduli-da-mappare', { params: { includi_decisi: decisi } });
      setModuli(r.data); setErr(null);
    } catch (e) { setErr(msgErrore(e, 'Impossibile leggere i moduli da mappare')); }
  }, [decisi]);
  useEffect(() => { carica(); }, [carica]);

  const salva = async (m, valore) => {
    try { await API.patch(`/campagne/moduli/${m.id}`, corpoTipo(valore)); await carica(); onCambio(); }
    catch (e) { setErr(msgErrore(e, 'Mappatura non riuscita')); }
  };

  return (
    <Sezione titolo={`Moduli da mappare (${moduli.filter(m => !m.senza_tipo_deciso).length})`} colore={COLORE_ADMIN}
      destra={<Interruttore etichetta="mostra anche i senza tipo decisi" valore={decisi} onChange={setDecisi} />}>
      {err && <div style={errore}>{err}</div>}
      <div style={{ ...card, overflowX: 'auto' }}>
        {moduli.length === 0 ? <div style={{ fontSize: 13, color: '#718096' }}>Nessun modulo da mappare.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thSx}>Titolo del modulo</th><th style={th}>Lead</th><th style={th}>Primo</th>
              <th style={th}>Ultimo</th><th style={thSx}>Tipo di ricorso</th>
            </tr></thead>
            <tbody>
              {moduli.map(m => (
                <tr key={m.id} style={{ color: m.senza_tipo_deciso ? '#718096' : undefined }}>
                  <td style={tdSx}>{m.titolo}</td>
                  <td style={td}>{fmtN(m.lead)}</td>
                  <td style={td}>{fmtData(m.primo)}</td>
                  <td style={td}>{fmtData(m.ultimo)}</td>
                  <td style={tdSx}><SelectTipo tipi={filtri.tipi} value={valoreTipo(null, m.senza_tipo_deciso)}
                    onChange={v => salva(m, v)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8 }}>
          Il tipo scelto vale anche per i lead già arrivati e l'attribuzione alle adesioni si ricalcola subito.
        </div>
      </div>
    </Sezione>
  );
}

function CampagneDaMappare({ filtri, onCambio }) {
  const [campagne, setCampagne] = useState([]);
  const [tutte, setTutte] = useState(false);
  const [err, setErr] = useState(null);
  const carica = useCallback(async () => {
    try {
      const r = await API.get('/campagne/campagne', { params: { da_mappare: !tutte } });
      setCampagne(r.data); setErr(null);
    } catch (e) { setErr(msgErrore(e, 'Impossibile leggere le campagne')); }
  }, [tutte]);
  useEffect(() => { carica(); }, [carica]);

  const salva = async (c, corpo) => {
    try { await API.patch(`/campagne/campagne/${c.id}`, corpo); await carica(); onCambio(); }
    catch (e) { setErr(msgErrore(e, 'Mappatura non riuscita')); }
  };

  return (
    <Sezione titolo={tutte ? `Campagne (${campagne.length})` : `Campagne da mappare (${campagne.length})`} colore={COLORE_ADMIN}
      destra={<Interruttore etichetta="mostra tutte le campagne" valore={tutte} onChange={setTutte} />}>
      {err && <div style={errore}>{err}</div>}
      <div style={{ ...card, overflowX: 'auto' }}>
        {campagne.length === 0 ? <div style={{ fontSize: 13, color: '#718096' }}>Nessuna campagna da mappare.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thSx}>Piattaforma</th><th style={thSx}>Campagna</th><th style={thSx}>Tipo campagna</th>
              <th style={th}>Spesa</th><th style={th}>Periodo</th><th style={thSx}>Tipo di ricorso</th>
            </tr></thead>
            <tbody>
              {campagne.map(c => (
                <tr key={c.id}>
                  <td style={tdSx}>{c.piattaforma === 'meta' ? 'Meta' : 'Google'}</td>
                  <td style={tdSx}>{c.nome}</td>
                  <td style={tdSx}>{c.tipo_campagna}
                    {!c.prefisso && <span style={{ color: COLORE_ADMIN, fontSize: 12 }}> (senza canale)</span>}</td>
                  <td style={td}>{fmtEur(c.spesa)}</td>
                  <td style={td}>{c.primo ? `${fmtData(c.primo)} – ${fmtData(c.ultimo)}` : ''}</td>
                  <td style={tdSx}><SelectTipo tipi={filtri.tipi} value={valoreTipo(c.tipo_ricorso_id, c.senza_tipo_deciso)}
                    onChange={v => salva(c, corpoTipo(v))} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8 }}>
          "Senza tipo, deciso": la spesa resta nel totale del canale e non viene più segnalata come da mappare.
          Il canale lo assegna la sync: Meta → Meta Ads, Google Search → Search, Demand Gen → Demand Gen;
          gli altri tipi di campagna restano senza canale.
        </div>
      </div>
    </Sezione>
  );
}

function UtmSource({ filtri, onCambio }) {
  const [valori, setValori] = useState([]);
  const [err, setErr] = useState(null);
  const carica = useCallback(async () => {
    try { const r = await API.get('/campagne/utm-source'); setValori(r.data); setErr(null); }
    catch (e) { setErr(msgErrore(e, 'Impossibile leggere i valori di utm_source')); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const prefissi = filtri.prefissi || [];
  const meta = p => prefissi.some(x => x.prefisso === p && x.fonte === 'meta');
  const salva = async (u, prefisso, piattaforma) => {
    try {
      await API.put(`/campagne/utm-source/${encodeURIComponent(u.utm_source)}`,
        { prefisso: prefisso || null, piattaforma: meta(prefisso) ? piattaforma || null : null });
      await carica(); onCambio();
    } catch (e) { setErr(msgErrore(e, 'Salvataggio non riuscito')); }
  };
  const nonCensiti = valori.filter(u => !u.censito).length;

  return (
    <Sezione titolo={`Canale da utm_source${nonCensiti ? ` (${nonCensiti} non censiti)` : ''}`} colore={COLORE_ADMIN}>
      {err && <div style={errore}>{err}</div>}
      <div style={{ ...card, overflowX: 'auto' }}>
        {valori.length === 0 ? <div style={{ fontSize: 13, color: '#718096' }}>Nessun valore di utm_source.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thSx}>utm_source</th><th style={th}>Lead</th><th style={th}>Ultimo</th>
              <th style={thSx}>Canale</th><th style={thSx}>Piattaforma Meta</th>
            </tr></thead>
            <tbody>
              {valori.map(u => (
                <tr key={u.utm_source}>
                  <td style={tdSx}>{u.utm_source}
                    {!u.censito && <span style={{ color: COLORE_ADMIN, fontSize: 12 }}> (non censito)</span>}</td>
                  <td style={td}>{fmtN(u.lead)}</td>
                  <td style={td}>{fmtData(u.ultimo)}</td>
                  <td style={tdSx}>
                    <select className="input-filtro" aria-label={`Canale di ${u.utm_source}`} value={voceCanale(prefissi, u.prefisso)}
                      onChange={e => salva(u, e.target.value, u.piattaforma)}>
                      <option value="">Sito, senza campagna</option>
                      {vociCanale(prefissi).map(p => <option key={p.prefisso} value={p.prefisso}>{p.canale}</option>)}
                    </select>
                  </td>
                  <td style={tdSx}>
                    {meta(u.prefisso) && (
                      <select className="input-filtro" aria-label={`Piattaforma di ${u.utm_source}`} value={u.piattaforma || ''}
                        onChange={e => salva(u, u.prefisso, e.target.value)}>
                        <option value="">non indicata</option>
                        {Object.entries(PIATTAFORME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8 }}>
          Ordine del canale di un lead: la campagna di utm_campaign, poi utm_source con questa tabella (fb e ig con un
          utm_medium diverso da paid sono Social organico), poi il dominio di provenienza con la tabella qui sotto o fbclid
          (Social organico), poi il prefisso dell'oggetto, altrimenti sito. Un valore non censito vale come sito.
          Il report si aggiorna subito.
        </div>
      </div>
    </Sezione>
  );
}

function Referrer({ filtri, onCambio }) {
  const [valori, setValori] = useState([]);
  const [nuovo, setNuovo] = useState('');
  const [err, setErr] = useState(null);
  const carica = useCallback(async () => {
    try { const r = await API.get('/campagne/referrer'); setValori(r.data); setErr(null); }
    catch (e) { setErr(msgErrore(e, 'Impossibile leggere i domini di provenienza')); }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const prefissi = filtri.prefissi || [];
  const salva = async (dominio, prefisso) => {
    try {
      await API.put(`/campagne/referrer/${encodeURIComponent(dominio)}`, { prefisso: prefisso || null });
      await carica(); onCambio();
      return true;
    } catch (e) { setErr(msgErrore(e, 'Salvataggio non riuscito')); return false; }
  };
  const aggiungi = async () => {
    const dominio = nuovo.trim().toLowerCase();
    if (dominio && await salva(dominio, null)) setNuovo('');
  };
  const nonCensiti = valori.filter(u => !u.censito).length;

  return (
    <Sezione titolo={`Canale da referrer${nonCensiti ? ` (${nonCensiti} non censiti)` : ''}`} colore={COLORE_ADMIN}>
      {err && <div style={errore}>{err}</div>}
      <div style={{ ...card, overflowX: 'auto' }}>
        {valori.length === 0 ? <div style={{ fontSize: 13, color: '#718096' }}>Nessun dominio di provenienza.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thSx}>Dominio</th><th style={th}>Lead</th><th style={th}>Ultimo</th><th style={thSx}>Canale</th>
            </tr></thead>
            <tbody>
              {valori.map(u => (
                <tr key={u.dominio}>
                  <td style={tdSx}>{u.dominio}
                    {!u.censito && <span style={{ color: COLORE_ADMIN, fontSize: 12 }}> (non censito)</span>}</td>
                  <td style={td}>{fmtN(u.lead)}</td>
                  <td style={td}>{fmtData(u.ultimo)}</td>
                  <td style={tdSx}>
                    <select className="input-filtro" aria-label={`Canale di ${u.dominio}`} value={voceCanale(prefissi, u.prefisso)}
                      onChange={e => salva(u.dominio, e.target.value)}>
                      <option value="">Sito, senza campagna</option>
                      {vociCanale(prefissi).map(p => <option key={p.prefisso} value={p.prefisso}>{p.canale}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
          <input className="input-filtro" aria-label="Nuovo dominio" placeholder="es. chatgpt.com" value={nuovo}
            onChange={e => setNuovo(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') aggiungi(); }} />
          <button style={pulsante(false, !nuovo.trim())} onClick={aggiungi} disabled={!nuovo.trim()}>Aggiungi dominio</button>
        </div>
        <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8 }}>
          Vale solo per i lead senza utm_source. Un dominio vale anche per i sottodomini (facebook.com copre
          l.facebook.com) e vince la riga più specifica (gemini.google.com su google.com); "www." si toglie da solo.
          Un dominio nuovo entra come sito: poi si sceglie il canale. I motori di ricerca restano sito.
          Lead: quelli con il dominio, anche con utm_source.
        </div>
      </div>
    </Sezione>
  );
}

function CollegamentiLead({ periodo, onCambio }) {
  const [livello, setLivello] = useState('da_verificare');
  const [stato, setStato] = useState('automatico');
  const [usaPeriodo, setUsaPeriodo] = useState(false);
  const [campione, setCampione] = useState(false);
  const [righe, setRighe] = useState([]);
  const [err, setErr] = useState(null);
  const [carico, setCarico] = useState(false);
  const [giro, setGiro] = useState(0);                // nuovo campione a ogni clic

  const carica = useCallback(async () => {
    setCarico(true);
    const params = {};
    if (campione) params.campione = 20;
    else { if (livello) params.livello = livello; if (stato) params.stato = stato; }
    if (usaPeriodo) { params.dal = periodo.dal; params.al = periodo.al; }
    try { const r = await API.get('/campagne/collegamenti', { params }); setRighe(r.data); setErr(null); }
    catch (e) { setErr(msgErrore(e, 'Impossibile leggere i collegamenti')); }
    finally { setCarico(false); }
  }, [livello, stato, usaPeriodo, campione, periodo.dal, periodo.al]);
  // giro: con il campione ogni clic ne chiede uno nuovo
  useEffect(() => { carica(); }, [carica, giro]);

  const decidi = async (r, azione) => {
    // codice_cliente: nei collettivi il collegamento è del ricorrente, non della scheda
    try { await API.post(`/campagne/collegamenti/${r.scheda_id}/${r.lead_id}/${azione}`, null,
                         { params: { codice_cliente: r.codice_cliente } }); await carica(); onCambio(); }
    catch (e) { setErr(msgErrore(e, 'Operazione non riuscita')); }
  };

  return (
    <Sezione titolo={`Collegamenti lead → adesione (${righe.length})`} colore={COLORE_ADMIN}
      destra={<div style={{ display: 'flex', gap: 8 }}>
        <button style={pulsante(campione)} onClick={() => { setCampione(true); setGiro(g => g + 1); }}>
          <Shuffle size={14} /> Campione casuale</button>
        {campione && <button style={pulsante(false)} onClick={() => setCampione(false)}>Torna ai filtri</button>}
      </div>}>
      {!campione && (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: '#718096' }}>LIVELLO<br />
            <select className="input-filtro" value={livello} onChange={e => setLivello(e.target.value)}>
              <option value="">Tutti</option>
              {Object.entries(LIVELLI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label style={{ fontSize: 11, color: '#718096' }}>STATO<br />
            <select className="input-filtro" value={stato} onChange={e => setStato(e.target.value)}>
              <option value="">Tutti</option>
              {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <Interruttore etichetta={`solo adesioni nel periodo del report`} valore={usaPeriodo} onChange={setUsaPeriodo} />
          <button style={pulsante(false, carico)} disabled={carico} onClick={carica}>
            <RefreshCw size={13} className={carico ? 'spin' : ''} /> Aggiorna</button>
        </div>
      )}
      {campione && <div style={{ fontSize: 12, color: '#4A5568', marginBottom: 8 }}>
        20 collegamenti automatici certi o probabili scelti a caso, per il controllo a vista.</div>}
      {err && <div style={errore}>{err}</div>}
      <div style={{ ...card, overflowX: 'auto' }}>
        {righe.length === 0 ? <div style={{ fontSize: 13, color: '#718096' }}>Nessun collegamento con questi filtri.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thSx}>Scheda</th><th style={thSx}>Cliente</th><th style={thSx}>Lead</th>
              <th style={thSx}>Contatti del lead</th><th style={thSx}>Livello</th><th style={thSx}>Stato</th><th style={th} />
            </tr></thead>
            <tbody>
              {righe.map(r => (
                <tr key={`${r.scheda_id}-${r.codice_cliente}-${r.lead_id}`}>
                  <td style={tdSx}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.codice_ricorso}</div>
                    <div style={{ fontSize: 11, color: '#718096' }}>{r.tipo} · {r.collettivo
                      ? `collettivo, adesione ${fmtData(r.adesione_il)}` : `creata ${fmtDataOra(r.scheda_creata_il)}`}</div>
                  </td>
                  <td style={tdSx}>{r.cliente}<div style={{ fontSize: 11, color: '#718096' }}>cod. {r.codice_cliente}</div></td>
                  <td style={tdSx}>
                    {fmtDataOra(r.lead_ricevuto_il)} · {r.canale}
                    <div style={{ fontSize: 11, color: '#718096' }}>{r.modulo}</div>
                  </td>
                  <td style={tdSx}>
                    {r.lead_nome}<div style={{ fontSize: 11, color: '#718096' }}>{r.lead_email} {r.lead_telefono}</div>
                  </td>
                  <td style={tdSx}><span style={{ color: COLORI_LIVELLO[r.livello], fontWeight: 600 }}>{LIVELLI[r.livello]}</span></td>
                  <td style={tdSx}>
                    {STATI[r.stato]}
                    {r.deciso_il && <div style={{ fontSize: 11, color: '#718096' }}>{r.deciso_da} · {fmtDataOra(r.deciso_il)}</div>}
                  </td>
                  <td style={td}>
                    {r.stato !== 'confermato' && <button style={pulsante(false)} title="Conferma: il lead è di questo cliente"
                      onClick={() => decidi(r, 'conferma')}><Check size={13} color="#276749" /></button>}{' '}
                    {r.stato !== 'rifiutato' && <button style={pulsante(false)} title="Rifiuta: il lead non è di questo cliente"
                      onClick={() => decidi(r, 'rifiuta')}><X size={13} color="#C53030" /></button>}{' '}
                    {r.stato !== 'automatico' && <button style={pulsante(false)} title="Ripristina il calcolo automatico"
                      onClick={() => decidi(r, 'ripristina')}><RotateCcw size={13} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 12, color: '#4A5568', marginTop: 8 }}>
          "Da verificare" non conta nel report finché non viene confermato. Un collegamento rifiutato non torna al ricalcolo.
        </div>
      </div>
    </Sezione>
  );
}

export default function CampagneAdmin({ filtri, periodo, onCambio }) {
  return (
    <div style={{ marginTop: 8, paddingTop: 12, borderTop: `3px solid ${COLORE_ADMIN}20` }}>
      <div style={{ fontSize: 12, color: COLORE_ADMIN, fontWeight: 700, marginBottom: 12 }}>
        Sezioni riservate (campagne.gestisci)
      </div>
      <ModuliDaMappare filtri={filtri} onCambio={onCambio} />
      <CampagneDaMappare filtri={filtri} onCambio={onCambio} />
      <UtmSource filtri={filtri} onCambio={onCambio} />
      <Referrer filtri={filtri} onCambio={onCambio} />
      <CollegamentiLead periodo={periodo} onCambio={onCambio} />
    </div>
  );
}
